// Der Stil-Constraint (Konzept §12.4). Zuerst festgelegt, dann alles
// durchgezogen — genau das ist die Antwort auf das KI-Asset-Problem.
//
// Regeln, die überall gelten:
//   1. Begrenzte Palette. Keine Farbe außerhalb dieser Datei.
//   2. Einheitliche Silhouettenstärke: Objekte sind blockig, keine feinen Netze.
//   3. Kein Detail unterhalb ~10 cm. Was kleiner ist, wird Textur oder weggelassen.
//   4. Matte Oberflächen, Glanz nur wo Metall gemeint ist.
//
// Dadurch lassen sich Assets aus verschiedenen Quellen (Blender, Code,
// generiert) zusammenwerfen und sehen trotzdem nach einem Ding aus.

import * as THREE from 'three';

/** Die Palette. Warme, staubige Erdtöne gegen kaltes Blaugrau. */
export const PALETTE = {
  // Gelände
  sandLight: 0xc4a882,
  sand: 0xa88f6a,
  sandDark: 0x8a7355,
  clay: 0x9c6b4a,
  rock: 0x7d7469,
  rockDark: 0x5c554d,
  scrub: 0x7a7c52,
  pine: 0x3d4f3a,
  pineDark: 0x2b3a2b,
  grass: 0x7e8455,
  snow: 0xd8dde0,

  // Bahn
  ballast: 0x6b6560,
  ballastDark: 0x514c48,
  rail: 0x8c8378,
  railRust: 0x7a5540,
  sleeper: 0x4a3c30,
  steel: 0x6e6f73,
  steelDark: 0x494a4e,
  concrete: 0x9a958c,
  concreteDark: 0x6d6862,

  // Konstruktion
  woodOld: 0x6b5540,
  woodDark: 0x4a3a2c,
  corrugated: 0x8b8478,
  coal: 0x22222a,
  coalDust: 0x33333c,

  // Himmel / Licht
  skyDay: 0x9fc4e0,
  skyDusk: 0xd8975a,
  skyNight: 0x141824,
  horizonDay: 0xd9d2c0,
  horizonDusk: 0xe0a068,
  horizonNight: 0x1c2130,

  sunDay: 0xfff4e0,
  sunDusk: 0xff9a4d,
  moonNight: 0xb8c8e0,

  fogDay: 0xc8cdc4,
  fogDusk: 0xc99a70,
  fogNight: 0x161b26,

  // Spieler
  skin: 0xb08a6a,
  cloth: 0x4a4f52,
  clothWorn: 0x3a3d40,
  denim: 0x3f4c5e,
};

/** Material-Fabrik mit Zwischenspeicher — gleiche Optik, wenige Draw Calls. */
const materialCache = new Map();

export function mat(colorKey, opts = {}) {
  const key = `${colorKey}|${JSON.stringify(opts)}`;
  if (materialCache.has(key)) return materialCache.get(key);
  const color = typeof colorKey === 'number' ? colorKey : PALETTE[colorKey];
  if (color === undefined) {
    console.warn(`[style] Unbekannte Farbe "${colorKey}" — Palette umgehen ist nicht erlaubt.`);
  }
  const m = new THREE.MeshLambertMaterial({
    color: color ?? 0xff00ff, // Magenta: Fehler sollen auffallen
    ...opts,
  });
  materialCache.set(key, m);
  return m;
}

/** Für Metall und alles, was Glanzlichter braucht. Sparsam einsetzen. */
export function matShiny(colorKey, opts = {}) {
  const key = `shiny|${colorKey}|${JSON.stringify(opts)}`;
  if (materialCache.has(key)) return materialCache.get(key);
  const color = typeof colorKey === 'number' ? colorKey : PALETTE[colorKey];
  const m = new THREE.MeshPhongMaterial({
    color: color ?? 0xff00ff,
    shininess: 24,
    specular: 0x333333,
    ...opts,
  });
  materialCache.set(key, m);
  return m;
}

/** Geometrie-Zwischenspeicher: dieselbe Box nie zweimal bauen. */
const geoCache = new Map();

export function box(w, h, d) {
  const key = `box|${w.toFixed(3)}|${h.toFixed(3)}|${d.toFixed(3)}`;
  if (!geoCache.has(key)) geoCache.set(key, new THREE.BoxGeometry(w, h, d));
  return geoCache.get(key);
}

export function cylinder(rTop, rBottom, h, seg = 8) {
  const key = `cyl|${rTop}|${rBottom}|${h}|${seg}`;
  if (!geoCache.has(key)) geoCache.set(key, new THREE.CylinderGeometry(rTop, rBottom, h, seg));
  return geoCache.get(key);
}

export function plane(w, h, segW = 1, segH = 1) {
  const key = `plane|${w}|${h}|${segW}|${segH}`;
  if (!geoCache.has(key)) geoCache.set(key, new THREE.PlaneGeometry(w, h, segW, segH));
  return geoCache.get(key);
}

/**
 * Farbwerte der Tageszeit. Wird von Himmel, Licht und Nebel gemeinsam benutzt,
 * damit nichts auseinanderläuft.
 * elevation: -1 (tiefe Nacht) .. 1 (Mittag)
 */
export function timeOfDayColors(elevation) {
  const dayness = clamp01((elevation + 0.12) / 0.5);      // 0 nachts, 1 tags
  const duskness = 1 - Math.abs(elevation) / 0.34;         // Spitze am Horizont
  const dusk = clamp01(duskness) * (1 - Math.abs(dayness - 0.5) * 0.4);

  const sky = mixThree(PALETTE.skyNight, PALETTE.skyDusk, PALETTE.skyDay, dayness, dusk);
  const horizon = mixThree(PALETTE.horizonNight, PALETTE.horizonDusk, PALETTE.horizonDay, dayness, dusk);
  const fog = mixThree(PALETTE.fogNight, PALETTE.fogDusk, PALETTE.fogDay, dayness, dusk);
  const sun = mixColor(PALETTE.sunDusk, PALETTE.sunDay, clamp01((elevation - 0.1) / 0.4));

  return {
    dayness,
    dusk,
    sky, horizon, fog, sun,
    // §12.4 „starkes Licht": Direktlicht dominiert Ambient klar (Silhouette).
    // Früher nachts ~1:1 (0.15 / 0.14) → flache, milchige Formen.
    // Ratio ~4:1 hält Form; zu wenig Ambient macht dunkle Waggons bei
    // hohem Sonnenstand zu reinen Schwarzflächen (Mittag-Seitenansicht).
    sunIntensity: 0.55 + dayness * 1.70 + dusk * 0.45,
    ambientIntensity: 0.12 + dayness * 0.40,
    // §12.1: hinter ~300 m Silhouette/Dunst. Zug ~180 m muss von der Seite
    // lesbar bleiben — Nachtsichtweite daher ≥ ~480 m Basis (Wetter zieht
    // runter, Floor in sky.js hält ≥ 320 m).
    fogNear: 60 + dayness * 100,
    fogFar: 520 + dayness * 700,
  };
}

function mixColor(a, b, t) {
  const ca = new THREE.Color(a), cb = new THREE.Color(b);
  return ca.lerp(cb, clamp01(t)).getHex();
}

function mixThree(night, duskC, day, dayness, dusk) {
  const base = new THREE.Color(night).lerp(new THREE.Color(day), clamp01(dayness));
  return base.lerp(new THREE.Color(duskC), clamp01(dusk) * 0.75).getHex();
}

function clamp01(v) { return v < 0 ? 0 : v > 1 ? 1 : v; }

export { clamp01 };

/**
 * Kleine Hilfe: leichte Farbvariation pro Objekt, damit hundert gleiche Büsche
 * nicht wie hundert gleiche Büsche aussehen. Deterministisch über den Seed.
 */
export function varyColor(baseKey, seed01, amount = 0.12) {
  const c = new THREE.Color(typeof baseKey === 'number' ? baseKey : PALETTE[baseKey]);
  const hsl = { h: 0, s: 0, l: 0 };
  c.getHSL(hsl);
  c.setHSL(
    (hsl.h + (seed01 - 0.5) * amount * 0.25 + 1) % 1,
    clamp01(hsl.s + (seed01 - 0.5) * amount * 0.5),
    clamp01(hsl.l + (seed01 - 0.5) * amount)
  );
  return c.getHex();
}

export const STYLE_RULES = Object.freeze({
  minDetailSize: 0.10,
  maxPolyPerScatter: 60,
  shadowDistance: 90,
  // VR-Vorbereitung (§14): Das Renderbudget muss von Anfang an billig sein.
  targetDrawCalls: 420,
  targetTriangles: 900000,
});
